"use client";

import { c as _c } from "react/compiler-runtime";
import DropPfp from "@/components/drops/create/utils/DropPfp";
import { DropAuthorBadges } from "@/components/waves/drops/DropAuthorBadges";
import type { ApiProfileMin } from "@/generated/models/ApiProfileMin";
import type { ApiProfileRepCategorySummary } from "@/generated/models/ApiProfileRepCategorySummary";
import { formatStatFloor } from "@/helpers/Helpers";
import { shortenAddress } from "@/helpers/address.helpers";
import { ProfileBadgeSize } from "@/components/common/profile/ProfileAvatar";
import { useCompactMode } from "@/contexts/CompactModeContext";
import Link from "next/link";
import UserCICAndLevel, { UserCICAndLevelSize } from "@/components/user/utils/UserCICAndLevel";
import IdentityProfileSupplement from "@/components/waves/drops/identity/IdentityProfileSupplement";
export type ParticipationIdentityProfileCardVariant = "default" | "chat";
type ParticipationIdentityProfileCardProfile = ApiProfileMin & {
  readonly bio?: string | null | undefined;
  readonly top_rep_categories?: readonly ApiProfileRepCategorySummary[] | null | undefined;
};
interface ParticipationIdentityProfileCardProps {
  readonly profile: ParticipationIdentityProfileCardProfile;
  readonly contextId?: string | number | undefined;
  readonly variant?: ParticipationIdentityProfileCardVariant | undefined;
  readonly showIdentityHeader?: boolean | undefined;
  readonly supplementFullWidth?: boolean | undefined;
}
interface IdentityStatLinkProps {
  readonly href: string;
  readonly label: string;
  readonly value: number;
  readonly rate?: number | undefined;
  readonly compact?: boolean | undefined;
}
const IDENTITY_STAT_LINK_CLASS = "tw-inline-flex tw-items-baseline tw-gap-1.5 tw-no-underline tw-transition-colors tw-duration-300 tw-ease-out desktop-hover:hover:tw-text-white";
const IDENTITY_STAT_VALUE_CLASS = "tw-font-semibold tw-leading-none tw-text-iron-100";
const IDENTITY_STAT_LABEL_CLASS = "tw-text-xs tw-font-medium tw-leading-none tw-tracking-wide tw-text-iron-500 tw-uppercase";
const IDENTITY_STAT_RATE_CLASS = "tw-text-xs tw-font-medium tw-leading-none tw-text-emerald-400";
function IdentityStatLink(t0) {
  const $ = _c(14);
  const {
    href,
    label,
    value,
    rate,
    compact: t1
  } = t0;
  const compact = t1 === undefined ? false : t1;
  const valueClass = `${compact ? "tw-text-xs" : "tw-text-sm"} ${IDENTITY_STAT_VALUE_CLASS}`;
  let t2;
  if ($[0] !== value) {
    t2 = formatStatFloor(value);
    $[0] = value;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t2 || $[3] !== valueClass) {
    t3 = <span className={valueClass}>{t2}</span>;
    $[2] = t2;
    $[3] = valueClass;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== label) {
    t4 = <span className={IDENTITY_STAT_LABEL_CLASS}>{label}</span>;
    $[5] = label;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== rate) {
    t5 = typeof rate === "number" && rate > 0 && <span className={IDENTITY_STAT_RATE_CLASS}>+{formatStatFloor(rate)}</span>;
    $[7] = rate;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== href || $[10] !== t3 || $[11] !== t4 || $[12] !== t5) {
    t6 = <Link href={href} prefetch={false} onClick={_temp} className={IDENTITY_STAT_LINK_CLASS}>{t3}{t4}{t5}</Link>;
    $[9] = href;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
}
function _temp(event) {
  return event.stopPropagation();
}
export default function ParticipationIdentityProfileCard(t0) {
  const $ = _c(55);
  const {
    profile,
    contextId,
    variant: t1,
    showIdentityHeader: t2,
    supplementFullWidth: t3
  } = t0;
  const variant = t1 === undefined ? "default" : t1;
  const showIdentityHeader = t2 === undefined ? true : t2;
  t3 === undefined ? false : t3;
  const compact = useCompactMode();
  const isChat = variant === "chat";
  const avatarSize = isChat && compact ? ProfileBadgeSize.COMPACT : ProfileBadgeSize.MEDIUM;
  const profileLabel = profile.handle ?? profile.primary_address;
  let t4;
  if ($[0] !== profileLabel) {
    t4 = profileLabel.toLowerCase();
    $[0] = profileLabel;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const routeIdentity = encodeURIComponent(t4);
  const rootHref = `/${routeIdentity}`;
  let t5;
  if ($[2] !== profile.handle || $[3] !== profile.primary_address) {
    t5 = !!profile.handle && profile.primary_address.toLowerCase() !== profile.handle.toLowerCase();
    $[2] = profile.handle;
    $[3] = profile.primary_address;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const shouldShowAddress = t5;
  let t6;
  if ($[5] !== profile.primary_address || $[6] !== shouldShowAddress) {
    t6 = shouldShowAddress ? shortenAddress(profile.primary_address) : null;
    $[5] = profile.primary_address;
    $[6] = shouldShowAddress;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const displayAddress = t6;
  let t7;
  if ($[8] !== profile.bio || $[9] !== profile.top_rep_categories) {
    t7 = !!profile.bio?.trim() || (profile.top_rep_categories ?? []).some(_temp2);
    $[8] = profile.bio;
    $[9] = profile.top_rep_categories;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const hasSupplementContent = t7;
  const shouldShowStatsDivider = showIdentityHeader || hasSupplementContent;
  const showBelowHeaderSupplement = hasSupplementContent;
  let t8;
  bb0: {
    if (!showIdentityHeader) {
      t8 = undefined;
      break bb0;
    }
    t8 = isChat ? "tw-mt-3" : "tw-mt-4";
  }
  const belowHeaderSupplementClassName = t8;
  let t10;
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="tw-group-hover:tw-opacity-100 tw-absolute tw-left-0 tw-right-0 tw-top-0 tw-h-px tw-bg-gradient-to-r tw-from-transparent tw-via-white/[0.15] tw-to-transparent tw-opacity-0 tw-transition-opacity tw-duration-500" />;
    t10 = <div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-bg-gradient-to-b tw-from-white/[0.03] tw-to-transparent" />;
    $[11] = t10;
    $[12] = t9;
  } else {
    t10 = $[11];
    t9 = $[12];
  }
  let t11;
  if ($[13] !== avatarSize || $[14] !== contextId || $[15] !== displayAddress || $[16] !== isChat || $[17] !== profile || $[18] !== profileLabel || $[19] !== rootHref || $[20] !== showIdentityHeader) {
    t11 = showIdentityHeader && <div className="tw-relative tw-z-10 tw-flex tw-items-start tw-justify-between tw-gap-3"><div className={`tw-flex tw-min-w-0 ${isChat ? "tw-items-center tw-gap-3" : "tw-items-start tw-gap-4"}`}><Link href={rootHref} prefetch={false} onClick={_temp3} className="tw-block tw-flex-shrink-0 tw-self-start tw-no-underline" aria-label={`View ${profileLabel}'s profile`}><DropPfp pfpUrl={profile.pfp} profileSize={avatarSize} /></Link><div className={`tw-flex tw-min-w-0 tw-flex-col ${isChat ? "tw-justify-center tw-gap-y-1" : "tw-gap-y-1.5"}`}><div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1"><Link href={rootHref} prefetch={false} onClick={_temp4} className="tw-mb-0 tw-text-iron-200 tw-no-underline tw-transition tw-duration-300 tw-ease-out desktop-hover:hover:tw-text-opacity-80 desktop-hover:hover:tw-underline"><span className="tw-text-sm tw-font-semibold tw-leading-none">{profileLabel}</span></Link><UserCICAndLevel level={profile.level} size={isChat ? UserCICAndLevelSize.COMPACT : UserCICAndLevelSize.SMALL} /><DropAuthorBadges profile={profile} tooltipIdPrefix={`identity-profile-card-${contextId ?? profile.id}`} className={isChat ? "tw-inline-flex tw-items-center tw-gap-x-1" : undefined} size={isChat ? "compact" : "default"} /></div>{displayAddress && <p title={profile.primary_address} className="tw-mb-0 tw-font-mono tw-text-xs tw-tracking-tight tw-text-iron-500">{displayAddress}</p>}</div></div></div>;
    $[13] = avatarSize;
    $[14] = contextId;
    $[15] = displayAddress;
    $[16] = isChat;
    $[17] = profile;
    $[18] = profileLabel;
    $[19] = rootHref;
    $[20] = showIdentityHeader;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== belowHeaderSupplementClassName || $[23] !== isChat || $[24] !== profile || $[25] !== showBelowHeaderSupplement) {
    t12 = showBelowHeaderSupplement && <div className={belowHeaderSupplementClassName}><IdentityProfileSupplement profile={profile} variant={isChat ? "chat" : "default"} maxRepCategories={2} /></div>;
    $[22] = belowHeaderSupplementClassName;
    $[23] = isChat;
    $[24] = profile;
    $[25] = showBelowHeaderSupplement;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  const t13 = `tw-flex tw-flex-wrap tw-items-center tw-gap-x-5 tw-gap-y-2 ${shouldShowStatsDivider ? "tw-mt-4 tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-white/5 tw-pt-3.5" : ""}`;
  const t14 = `${rootHref}/collected`;
  let t15;
  if ($[27] !== isChat || $[28] !== profile.tdh || $[29] !== profile.tdh_rate || $[30] !== t14) {
    t15 = <IdentityStatLink href={t14} label="TDH" value={profile.tdh} rate={profile.tdh_rate} compact={isChat} />;
    $[27] = isChat;
    $[28] = profile.tdh;
    $[29] = profile.tdh_rate;
    $[30] = t14;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  const t16 = `${rootHref}/xtdh`;
  let t17;
  if ($[32] !== isChat || $[33] !== profile.xtdh || $[34] !== profile.xtdh_rate || $[35] !== t16) {
    t17 = <IdentityStatLink href={t16} label="xTDH" value={profile.xtdh} rate={profile.xtdh_rate} compact={isChat} />;
    $[32] = isChat;
    $[33] = profile.xtdh;
    $[34] = profile.xtdh_rate;
    $[35] = t16;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== isChat || $[38] !== profile.cic || $[39] !== rootHref) {
    t18 = <IdentityStatLink href={rootHref} label="NIC" value={profile.cic} compact={isChat} />;
    $[37] = isChat;
    $[38] = profile.cic;
    $[39] = rootHref;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== isChat || $[42] !== profile.rep || $[43] !== rootHref) {
    t19 = <IdentityStatLink href={rootHref} label="Rep" value={profile.rep} compact={isChat} />;
    $[41] = isChat;
    $[42] = profile.rep;
    $[43] = rootHref;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] !== t13 || $[46] !== t15 || $[47] !== t17 || $[48] !== t18 || $[49] !== t19) {
    t20 = <div className={t13}>{t15}{t17}{t18}{t19}</div>;
    $[45] = t13;
    $[46] = t15;
    $[47] = t17;
    $[48] = t18;
    $[49] = t19;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  let t21;
  if ($[51] !== t11 || $[52] !== t12 || $[53] !== t20) {
    t21 = <div className="tw-mt-3 tw-w-full tw-@container"><div className="tw-group tw-relative tw-flex tw-w-full tw-cursor-pointer tw-flex-col tw-overflow-hidden tw-rounded-lg tw-border tw-border-solid tw-border-white/5 tw-bg-[#18181b] tw-p-3 tw-shadow-2xl tw-shadow-black/40 tw-transition-all tw-duration-300 @sm:tw-p-5">{t9}{t10}{t11}{t12}{t20}</div></div>;
    $[51] = t11;
    $[52] = t12;
    $[53] = t20;
    $[54] = t21;
  } else {
    t21 = $[54];
  }
  return t21;
}
function _temp4(event_0) {
  return event_0.stopPropagation();
}
function _temp3(event) {
  return event.stopPropagation();
}
function _temp2(category) {
  return category.category.trim().length > 0;
}